import moment from 'moment';
import queryString from 'query-string';

import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { ContentLocalesI } from './interfaces/session.types';
import { KeyTextExtI } from './interfaces/translate.interface';
import {TFunction} from "i18next";

export const mapIdToData = <DataRecord extends { id: string }>(
  data: DataRecord[] = [],
): Record<string, DataRecord> => mapKeyToData(data, 'id');

export const mapKeyToData = <DataRecord extends Record<IndexKey, string>, IndexKey extends keyof DataRecord>(
  data: DataRecord[] = [],
  key: IndexKey,
): Record<string, DataRecord> =>
  data.reduce((acc: Record<string, DataRecord>, item: DataRecord) => {
    acc[item[key]] = item;
    return acc;
  }, {});

export const mapKeyToDataValue = <
  DataRecord extends Record<IndexKey, string>,
  IndexKey extends keyof DataRecord,
  ValueKey extends keyof DataRecord,
>(
  data: DataRecord[] = [],
  indexKey: IndexKey,
  valKey: ValueKey,
): { [idx: string]: DataRecord[ValueKey] } =>
  data.reduce((acc: { [idx: string]: DataRecord[ValueKey] }, item: DataRecord) => {
    acc[item[indexKey]] = item[valKey];
    return acc;
  }, {});

export const trimValues = <T extends Record<string, any>>(obj: T): T =>
  Object.keys(obj).reduce((acc: T, key: keyof T) => {
    if (typeof obj[key] === 'string') {
      acc[key] = obj[key].trim();
    } else if (Array.isArray(obj[key])) {
      acc[key] = obj[key];
    } else if (obj[key] && typeof obj[key] === 'object') {
      acc[key] = trimValues(obj[key]);
    } else {
      acc[key] = obj[key];
    }
    return acc;
  }, {} as T);

export async function asyncForEach(array: any[], callback: (item: any) => Promise<any>) {
  const results = array.map(callback);
  await Promise.all(results);
}

export const addMeasureType = (measure: string | number): string =>
  typeof measure === 'number' ? `${measure}px` : measure;

export const responseRateFormat = (rate: number): string | number => {
  rate = rate * 100;
  return rate % 1 ? rate.toFixed(2) : rate;
};

export function finalizeAll<T>(promises: Array<Promise<T>>): Promise<{ resolves: T[]; rejects: any[] }> {
  const promisesLength = promises.length;
  let count: number = 0;
  return new Promise((res) => {
    const resolves: T[] = [];
    const rejects: any[] = [];
    promises.forEach((promise, idx) => {
      promise
        .then((result) => {
          resolves[idx] = result;
        })
        .catch((error) => {
          rejects[idx] = error;
        })
        .finally(() => {
          count += 1;
          if (count >= promisesLength) {
            res({ resolves, rejects });
          }
        });
    });
  });
}

export const txtOrKey = ({ txt, key }: KeyTextExtI, t: TFunction): string => txt || t(key);

export const promiseWithDelay = (call: Promise<unknown>, delayInMs = 2000) =>
  new Promise<void>((res) => {
    void finalizeAll([new Promise(resolve => setTimeout(resolve, delayInMs)), call]).finally(() => {
      res();
    });
  });

type StringEncodeableValue = Exclude<boolean|string|number, ''>;

/**
 * create a querystring from the passed filterObject
 * all props set to null will be removed
 * @param filterObject
 * @param commaSeparatedArrayKeys array values that will be written as comma-separated list instead of multiple parameters
 * @returns queryString
 */
export function getUrlFromObject<
  ParameterObject extends Record<string, Array<StringEncodeableValue|undefined|null>|StringEncodeableValue|undefined|null>,
  Key extends keyof ParameterObject,
>(filterObject?: ParameterObject, commaSeparatedArrayKeys: Key[] = []) {
  if (!filterObject) {
    return '';
  }

  const isNonEmptyEncodeable = (value: unknown): value is StringEncodeableValue => value !== '' && ['string', 'boolean', 'number'].indexOf(typeof value) !== -1;
  const encodeKeyValue = (key: Key, value: StringEncodeableValue) =>
    `${encodeURIComponent(String(key))}=${encodeURIComponent(value)}`;

  return (
    '?' +
    (Object.keys(filterObject) as Key[])
      .map((key: Key) => {

        const value = filterObject[key];
        if (Array.isArray(value)) {
          if (commaSeparatedArrayKeys.indexOf(key) === -1) {
            return value
              .filter(isNonEmptyEncodeable)
              .map((val: StringEncodeableValue) => encodeKeyValue(key, val))
              .join('&');
          } else {
            return (
              encodeURIComponent(String(key)) +
              '=' +
              value.filter(isNonEmptyEncodeable).map(encodeURIComponent).join(',')
            );
          }
        }
        if (isNonEmptyEncodeable(value)) {
          return encodeKeyValue(key, value);
        }
        return '';
      })
      .filter((s) => s !== '')
      .join('&')
  );
}

export const checkDateFormat = (value: string | null) => {
  if (value) {
    if (value !== 'Invalid date') {
      return moment(value);
    }
    return value;
  }
  return null;
};

/**
 * unfolds object with dots as key separator up to a depth of 3
 *
 * example:
 *
 * { 'a.b': 42 } => { a: { b: 42 } }
 *
 */
export function unfoldErrorResponse(foldedObject: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  Object.keys(foldedObject).forEach((key) => {
    let splittedKey = key.split('.');
    const val = foldedObject[key];
    if (splittedKey.length === 2) {
      if (!result[splittedKey[0]]) {
        result[splittedKey[0]] = {};
      }
      [result[splittedKey[0]][splittedKey[1]]] = val;
    } else if (splittedKey.length === 3) {
      if (!result[splittedKey[0]]) {
        result[splittedKey[0]] = { [splittedKey[1]]: {} };
      }
      [result[splittedKey[0]][splittedKey[1]][splittedKey[2]]] = val;
    } else {
      if (!val.arguments || !val.arguments.field_name) {
        [result[key]] = val;
        return;
      }
      splittedKey = val.arguments.field_name.split('.');
      if (splittedKey.length === 2) {
        if (!result[splittedKey[0]]) {
          result[splittedKey[0]] = {};
        }
        result[splittedKey[0]][splittedKey[1]] = val.translation_key;
      }
    }
  });
  return result;
}

export function clearContentLocalesI(translations: ContentLocalesI): ContentLocalesI {
  const newTranslations: ContentLocalesI = {};
  Object.keys(translations).forEach((key) => {
    newTranslations[key] = '';
  });
  return newTranslations;
}

interface LocationStateOpts {
  replace: boolean;
}

export const setLocationState = (
  params: Record<string, string|string[]|undefined>,
  { replace }: LocationStateOpts = { replace: false },
): void => {
  const url = new URL(window.location.href);

  if (
    typeof window === 'undefined' ||
    typeof window.location === 'undefined' ||
    typeof window.location.href !== 'string'
  ) {
    // Handle the scenario when window or window.location is not available
    console.error('Unable to access window.location.href. setLocationState aborted.');
    return;
  }

  Object.entries(params).forEach(([key, val]) => {
    if (val === null || val === undefined) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, val);
    }
  });

  window.history[replace ? 'replaceState' : 'pushState']({}, '', url.pathname + url.search + url.hash);
};

export const getLocationState = (): Record<string, string | string[] | undefined> => {
  return queryString.parse(window.location.search);
};

const SEARCH_PARAMS_CUSTOM_EVENT_KEY = 'newSearchParams';
type SearchDetailParams = Record<string, string|string[]|undefined>;

/**
 * Hook to manipulate location search params. Watches changes to search params locally.
 * To watch a search param globally (i.e when a search param is updated by any other component),
 * please use `useWatchSearchParam`
 */
export const useSearchParams = () => {
  const [internalState, setInternalState] = useState(getLocationState());

  const setState = (params: SearchDetailParams, opts?: LocationStateOpts) => {
    setLocationState(params, opts);
    const newState = produce(internalState, () => getLocationState());

    const event = new CustomEvent(SEARCH_PARAMS_CUSTOM_EVENT_KEY, { detail: params });

    window.dispatchEvent(event);

    setInternalState(newState);
  };

  return [internalState, setState] as const;
};

/**
 * Watch a location search param globally. i.e when this search param is changed by
 * any component, returned value is updated.
 */
export const useWatchSearchParam = (param: string) => {
  const [internalState, setInternalState] = useState(getLocationState()[param]);

  useEffect(() => {
    const listener = (event: CustomEvent<SearchDetailParams>) => {
      const newParams = event.detail;
      // Notify user of hook when any of the requested params have changed
      if (newParams[param] !== undefined) setInternalState(newParams[param]);
    };

    window.addEventListener(SEARCH_PARAMS_CUSTOM_EVENT_KEY, listener);

    return () => {
      window.removeEventListener(SEARCH_PARAMS_CUSTOM_EVENT_KEY, listener);
    };
  }, []);

  return internalState;
};

export function unique<T>(arr: T[], isEqual: (a: T, b: T) => boolean): T[] {
  return arr.filter((currentLine, index) => {
    return arr.slice(0, index).every((previousLine) => !isEqual(previousLine, currentLine));
  });
}

export function encodeToBase64(str: string) {
  return btoa(encodeURIComponent(str));
}

export function decodeFromBase64(encodedStr: string) {
  return decodeURIComponent(atob(encodedStr));
}

export const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
